import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ServiceForm } from './ServiceForms';

const Dashboard: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [activeService, setActiveService] = useState<string | null>(null);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const services = [
    {
      id: 'bank-statement-analysis',
      name: 'Bank Statement Analysis',
      description: 'Parse PDF bank statements and get comprehensive financial insights',
      icon: '🏦',
      category: 'PehchanPe Financial',
      endpoints: ['parsePDF', 'getAllBankAccounts', 'analysis', 'transactions']
    },
    {
      id: 'credit-bureau',
      name: 'Credit Bureau Report',
      description: 'Get comprehensive credit reports and creditworthiness analysis',
      icon: '📊',
      category: 'PehchanPe Financial',
      endpoints: ['creditReport']
    },
    {
      id: 'driving-license',
      name: 'Driving License Verification',
      description: 'Verify driving license details with date of birth',
      icon: '🚗',
      category: 'PehchanPe eKYC',
      endpoints: ['ekyc']
    },
    {
      id: 'pan-verification',
      name: 'PAN Card Verification',
      description: 'Verify PAN card details',
      icon: '🆔',
      category: 'PehchanPe eKYC',
      endpoints: ['ekyc']
    },
    {
      id: 'voter-id',
      name: 'Voter ID Verification',
      description: 'Verify voter ID card with state information',
      icon: '🗳️',
      category: 'PehchanPe eKYC',
      endpoints: ['ekyc']
    },
    {
      id: 'fssai-verification',
      name: 'FSSAI License Verification',
      description: 'Verify Food Safety and Standards Authority license',
      icon: '🍽️',
      category: 'PehchanPe eKYC',
      endpoints: ['ekyc']
    }
    // {
    //   id: 'drug-license',
    //   name: 'Drug License Verification',
    //   description: 'Verify pharmaceutical drug license by state',
    //   icon: '💊',
    //   category: 'PehchanPe eKYC',
    //   endpoints: ['ekyc']
    // }
  ];

  const ServiceTestPanel = ({ service }: { service: any }) => {
    const [testResult, setTestResult] = useState<string | React.ReactNode>('');
    const [isLoading, setIsLoading] = useState(false);

    const handleResult = (result: string | React.ReactNode) => {
      setTestResult(result);
    };

    const handleLoading = (loading: boolean) => {
      setIsLoading(loading);
    };

    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center mb-4">
          <span className="text-2xl mr-3">{service.icon}</span>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{service.name}</h3>
            <p className="text-sm text-gray-600">{service.description}</p>
            <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mt-2">
              {service.category}
            </span>
          </div>
        </div>
        
        <div className="space-y-4">
          <ServiceForm
            service={service}
            onResult={handleResult}
            onLoading={handleLoading}
          />
          
          {isLoading && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-2 text-gray-600">Processing...</span>
            </div>
          )}
          
          {testResult && !isLoading && (
            <div>
              {typeof testResult === 'string' ? (
                <div className={`p-4 rounded-md max-h-96 overflow-y-auto ${
                  testResult.startsWith('❌') 
                    ? 'bg-red-50 border border-red-200' 
                    : 'bg-gray-50 border border-gray-200'
                }`}>
                  <h4 className={`text-sm font-medium mb-2 ${
                    testResult.startsWith('❌') ? 'text-red-900' : 'text-gray-900'
                  }`}>
                    {testResult.startsWith('❌') ? 'Error Response:' : 'API Response:'}
                  </h4>
                  <pre className={`text-sm whitespace-pre-wrap ${
                    testResult.startsWith('❌') ? 'text-red-700' : 'text-gray-700'
                  }`}>
                    {testResult}
                  </pre>
                </div>
              ) : (
                <div className="w-full">
                  {testResult}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Service Testing Dashboard</h1>
              <p className="text-sm text-gray-600">Welcome, {currentUser?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={`${activeService === 'bank-statement-analysis' ? 'w-full' : 'max-w-7xl mx-auto'} py-6 sm:px-6 lg:px-8`}>
        <div className={`${activeService === 'bank-statement-analysis' ? 'px-4' : 'px-4 py-6 sm:px-0'}`}>
          {!activeService ? (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Available Services</h2>
                <p className="text-gray-600">Select a service below to start testing our APIs and functionality.</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((service) => (
                  <div
                    key={service.id}
                    onClick={() => setActiveService(service.id)}
                    className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer border border-gray-200 hover:border-indigo-300"
                  >
                    <div className="flex items-center mb-3">
                      <span className="text-3xl mr-3">{service.icon}</span>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{service.name}</h3>
                        <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                          {service.category}
                        </span>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                    <div className="flex items-center text-indigo-600 text-sm font-medium">
                      <span>Test Service</span>
                      <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div>
              <div className="mb-6">
                <button
                  onClick={() => setActiveService(null)}
                  className="flex items-center text-indigo-600 hover:text-indigo-800 mb-4"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Services
                </button>
              </div>
              
              <ServiceTestPanel service={services.find(s => s.id === activeService)} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
